/**
 * The Dashboard's view model (spec 179 §13, Phase 3) — PURE.
 *
 * `buildDashboardView` turns the overview (`GET /analytics/v2/reporting-overview`,
 * shape I-3) into the strings, tones, rows and hrefs every component renders,
 * so the rules the page shows — a delta's tone by MEANING, the status rows in
 * the server's order, an insight's link per kind — are pinned by
 * `dashboardModel.test.ts` without mounting the page.
 *
 * Also here: the URL scope (I-1 — calendar days on the browser's clock, the
 * range picker's own days) and the drill-through scope (I-2 — ISO instants).
 * The page never re-derives a number the overview ships (D9): the only
 * arithmetic below is a percentage the mockup prints and the API does not
 * carry (handled %, after-hours %, a category's share) and the difference
 * between two shipped values for a delta's text.
 */
import dayjs, { type Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import { categoryLabel, statusLabel } from '../../lib/workOrderLabels';
import { inspectionTypeLabel } from '../../lib/inspectionTypes';
import type { DashboardScope } from '../reports/reportDefinition';
import type { MetricKey } from './metricDefinitions';
import { firstIndex, lastIndex, spendBenchmarkTotal } from './headlineTrend';

// ─── The overview (I-3) ──────────────────────────────────────────────────────
export type Delta = { pct: number; direction: 'up' | 'down' | 'flat' };
export type LabelAmount = { label: string; amount: number };
export interface IndustryReference { value: number; text: string; source: string; sourceShort: string; year: number }

export interface ReportingOverview {
  range: { startDate: string; endDate: string; propertyCount: number; scopedToProperty: boolean; scopedToPortfolio: boolean; portfolioCount: number };
  pulse: {
    openWorkOrders: number; overdue: number; emergencies: number;
    mttrHours: number; mttrMeanHours: number; mttrDelta: Delta; mttrPrevHours: number | null; mttrLastYear: number | null;
    // Money — null for a reader below spec 113's D-04 line (the server strips it).
    spend: number | null; spendDelta: Delta | null; spendPrev: number | null; spendLastYear: number | null; spendPerUnit: number | null;
    makeReadyDays: number; makeReadyInProgress: number; makeReadyDelta: Delta; makeReadyPrevDays: number | null; makeReadyMedianDays: number | null; makeReadyMedianPrevDays: number | null; makeReadyMedianLastYear: number | null; makeReadyLastYear: number | null;
    satisfaction: number; satisfactionCount: number; satisfactionDelta: Delta; satisfactionPrev: number | null; satisfactionLastYear: number | null;
    openOldestDays: number | null; liveUnits: number;
    emergencyShare: number | null; workOrdersPerUnit: number | null;
  };
  spend: { total: number | null; property: LabelAmount[]; category: (LabelAmount & { category: string })[]; vendor: LabelAmount[]; vendorType: LabelAmount[] };
  /** `avgDays` is the MEDIAN (spec 179 §11 #4); `trade` is the server's humanized English label, `category` the raw enum a row links with. */
  speedByTrade: { trade: string; category: string; avgDays: number; meanDays: number; count: number }[];
  makeReady: { avgDays: number; inProgress: number; completedCount: number; list: { unit: string; property: string; days: number; stage: string | null }[] };
  workOrders: { inProgress: number; completed: number; byStatus: { status: string; count: number }[] };
  inspections: { completed: number; onTimePct: number | null; onTimeCount: number; onTimeEligible: number; byType: { type: string; count: number }[]; findingsTotal: number; findingsRaised: number };
  vendorOnTime: { pct: number | null; n: number };
  vendors: { id: string; name: string; jobs: number; rating: number; ratingCount: number; onTimePct: number | null; onTimeN: number; spend: number | null; needsAttention: boolean }[];
  trend: { month: string; spend: number | null; count: number; completed: number; mttrHours: number | null; satisfaction: number | null; satisfactionCount: number }[];
  insights: string[];
  /** `facts` = the numbers behind `text`, so the sentence is written here in the viewer's language (the server's `text` is English). */
  insightDetails: { text: string; kind: 'trade' | 'vendor' | 'category'; key: string; label: string; facts?: { days?: string; mult?: string; pct?: number; visits?: number; spend?: number | null; share?: number; amount?: number } }[];
  aiActivity: {
    voiceCalls: number; voiceMinutes: number; email: number; sms: number; inAppChat: number; total: number; becameWorkOrders: number;
    outsideHours: number; businessHoursStart: number; businessHoursEnd: number; businessHoursTimezone: string;
    closedByAiTriage: number; residentSelfService: number;
  };
  sparks: { openWorkOrders: number[]; mttr: number[]; spend: number[]; emergencies: number[]; satisfaction: number[]; makeReady: number[] };
  industry: { timeToComplete: IndustryReference; residentRating: IndustryReference; spendPerUnitMonth: IndustryReference };
}

// ─── The URL scope (I-1) ─────────────────────────────────────────────────────
export type RangePresetKey = 'last7Days' | 'last30Days' | 'last90Days' | 'yearToDate';
export const RANGE_PRESETS: { key: RangePresetKey; range: (now: Dayjs) => [Dayjs, Dayjs] }[] = [
  { key: 'last7Days', range: (now) => [now.subtract(7, 'day'), now] },
  { key: 'last30Days', range: (now) => [now.subtract(30, 'day'), now] },
  { key: 'last90Days', range: (now) => [now.subtract(90, 'day'), now] },
  { key: 'yearToDate', range: (now) => [now.startOf('year'), now] },
];
/** The page's default period — the same 30 days it has always opened on. */
export const defaultRange = (now: Dayjs = dayjs()): [Dayjs, Dayjs] => [now.subtract(30, 'day'), now];
/** Which preset the range IS (by calendar day), if any — what "Set as my default" remembers. */
export function presetKeyFor(range: [Dayjs, Dayjs], now: Dayjs = dayjs()): RangePresetKey | undefined {
  return RANGE_PRESETS.find((p) => {
    const [from, to] = p.range(now);
    return range[0].isSame(from, 'day') && range[1].isSame(to, 'day');
  })?.key;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
export interface UrlScope { propertyId?: string; myPortfolio: boolean; from?: string; to?: string }
export function readScopeFromUrl(search: string): UrlScope {
  const p = new URLSearchParams(search);
  const day = (v: string | null) => (v && DAY.test(v) && dayjs(v).isValid() ? v : undefined);
  return {
    propertyId: p.get('propertyId') || undefined,
    myPortfolio: p.get('myPortfolio') === 'true',
    from: day(p.get('from')),
    to: day(p.get('to')),
  };
}
export function writeScopeToUrl(scope: DashboardScope): URLSearchParams {
  const p = new URLSearchParams();
  if (scope.propertyId) p.set('propertyId', scope.propertyId);
  else if (scope.myPortfolio) p.set('myPortfolio', 'true');
  p.set('from', scope.range[0].format('YYYY-MM-DD'));
  p.set('to', scope.range[1].format('YYYY-MM-DD'));
  return p;
}

// ─── The drill-through scope (I-2) ───────────────────────────────────────────
export interface DrillScope { propertyId?: string; myPortfolio: boolean; dateFrom: string; dateTo: string }
/** The picker's days as the instants the lists filter on: the start of the first day to the end of the last. */
export function drillScope(scope: DashboardScope): DrillScope {
  return {
    propertyId: scope.propertyId,
    myPortfolio: !scope.propertyId && scope.myPortfolio,
    dateFrom: scope.range[0].startOf('day').toISOString(),
    dateTo: scope.range[1].endOf('day').toISOString(),
  };
}
function withParams(path: string, entries: Array<[string, string | undefined]>): string {
  const p = new URLSearchParams();
  for (const [k, v] of entries) if (v) p.set(k, v);
  const q = p.toString();
  return q ? `${path}?${q}` : path;
}
/** `/work-orders` with the scope (+ the period unless the metric is a count of NOW) — `scope=all` defeats the list's "mine" default. */
export function workOrdersHref(d: DrillScope, params: Record<string, string | undefined> = {}, period = true): string {
  return withParams('/work-orders', [
    ['propertyId', d.propertyId],
    ['dateFrom', period ? d.dateFrom : undefined],
    ['dateTo', period ? d.dateTo : undefined],
    ['scope', d.myPortfolio ? 'portfolio' : 'all'],
    ...Object.entries(params),
  ]);
}
export function inspectionsHref(d: DrillScope, params: Record<string, string | undefined> = {}): string {
  return withParams('/inspections', [
    ['propertyId', d.propertyId],
    ['dateFrom', d.dateFrom],
    ['dateTo', d.dateTo],
    ['scope', d.myPortfolio ? 'portfolio' : 'all'],
    ...Object.entries(params),
  ]);
}
/**
 * `/invoices` — the spend door (PO 2026-09-17, "B"): spend = bills APPROVED (or paid) in the
 * period on work orders of any age, so the list opens on the invoice anchor window with the
 * two spend statuses, never on work orders created in the period. Money roles only — the
 * Cost section and the Spend tile exist only above the money line.
 */
export function invoicesHref(d: DrillScope, params: Record<string, string | undefined> = {}): string {
  return withParams('/invoices', [
    ['status', 'SPEND'],
    ['propertyId', d.propertyId],
    ['myPortfolio', !d.propertyId && d.myPortfolio ? 'true' : undefined],
    ['dateFrom', d.dateFrom],
    ['dateTo', d.dateTo],
    ...Object.entries(params),
  ]);
}
/** In-progress is NOW — no period. */
export function makeReadyHref(d: DrillScope, stage?: string | null): string {
  return withParams('/make-ready', [
    ['propertyId', d.propertyId],
    ['myPortfolio', !d.propertyId && d.myPortfolio ? 'true' : undefined],
    ['stage', stage ?? undefined],
  ]);
}

/**
 * The building page strip's scope (spec 179 §5.3): this property, this
 * calendar month to date (the browser's clock — no portfolio, no controls),
 * plus the Dashboard URL it and the tab's "Open in Dashboard" link share, so
 * neither can point at a different period than the other.
 */
export function buildingMonthScope(propertyId: string, now: Dayjs = dayjs()): { scope: DashboardScope; dashboardHref: string } {
  const scope: DashboardScope = { propertyId, myPortfolio: false, range: [now.startOf('month'), now] };
  return { scope, dashboardHref: `/dashboard?${writeScopeToUrl(scope).toString()}` };
}

// ─── Formatters (the page's own, unchanged) ──────────────────────────────────
export const fmtMoney = (n: number) => '$' + Math.round(n).toLocaleString();
export const fmtMoneyShort = (n: number) => (n >= 1000 ? '$' + (n / 1000).toFixed(1) + 'k' : '$' + Math.round(n));
export const days1 = (d: number) => d.toFixed(1);
export const hoursToDays = (h: number) => h / 24;
/**
 * The Dashboard's ONE duration rule for a value measured in hours: under a
 * day it reads in whole hours ("18 h"), else in days to one decimal ("2.7 d").
 * The headline tile, the drawer's cells and the sub-pages all read this — a
 * job done in twenty minutes printed "0.0 days" beside a sub-page saying
 * "0 h" (the PO's phone screenshot, 2026-09-17).
 */
export const fmtHours = (t: TFunction, hours: number) =>
  hours < 24 ? t('dashboard:units.h', { value: Math.round(hours) }) : t('dashboard:units.d', { value: days1(hoursToDays(hours)) });

// ─── The view ────────────────────────────────────────────────────────────────
export interface DeltaChip { text: string; tone: 'ok' | 'neutral' }
/** One month on the headline tile's labeled line: the plotted value and the finished strings for it. */
export interface HeadlineTrendPoint { label: string; tip: string; value: number | null; text: string }
/** The labeled 12-month line (Time to complete, Spend): `benchmark` is in the same unit as the points' values. */
export interface HeadlineTrend { points: HeadlineTrendPoint[]; benchmark: number | null; benchmarkLabel: string }
export interface HeadlineTile {
  key: 'timeToComplete' | 'residentRating' | 'spend';
  label: string; value: string; unit?: string;
  delta: DeltaChip | null; context: string;
  lastYearLabel: string; lastYear: string; industry: string; spark: number[];
  /** The labeled line in the sparkline's place; absent = the plain sparkline (Resident rating keeps it). */
  trend?: HeadlineTrend;
}
export interface StripTileView {
  key: MetricKey; sectionId: string; label: string; value: string;
  danger: boolean; pill?: string; why: string; link?: { label: string; href: string }; nodata?: boolean;
}
export interface BarRow { key: string; label: string; n?: string; value: number; valueText: string; href?: string; pill?: string }
export interface InsightView { text: string; linkLabel: string; href: string }
export interface TrendPoint { label: string; tip: string; value: number | null }
export interface VendorRow { id: string; name: string; jobs: number; rating: string; onTime: string; spend: string; needsAttention: boolean; href: string }

export interface DashboardView {
  headlines: HeadlineTile[];
  strip: StripTileView[];
  insights: InsightView[];
  workOrders: {
    answer: string; byStatus: BarRow[]; byStatusSub: string;
    speed: BarRow[]; speedReference: { at: number; label: string } | null;
    trend: { month: string; created: number; completed: number }[];
  };
  ai: {
    answer: string; channels: BarRow[]; channelsFoot: string;
    afterHours: string; noVisit: { value: string; pills: string[] };
  };
  makeReady: { answer: string; turnsSub: string; turns: { key: string; unit: string; days: string; stage: string; href: string }[]; /** "All N turns ›" when the card shows fewer rows than are open (the overview ships the six longest-running). */ allTurns: { label: string; href: string } | null };
  inspections: {
    answer: string;
    stats: { completed: string; onTime: string; onTimeSub: string; findings: string; raised: string; raisedSub: string };
    byType: { type: string; label: string; count: number; href: string }[];
  };
  cost: {
    answer: string; total: string; byCategory: BarRow[];
    trend: TrendPoint[]; vendors: VendorRow[];
  } | null;
}

const monthLabel = (month: string) => dayjs(month + '-01').format('MMM');
const monthTip = (month: string) => dayjs(month + '-01').format('MMM YYYY');

/**
 * The delta chip's tone follows what the number MEANS: fewer days, less spend
 * and a higher rating are `ok`; "no change" and a move the other way are
 * neutral (the arrow carries the direction — `danger` is reserved for overdue,
 * emergencies and "needs attention"). No previous window → no chip.
 */
function deltaChip(t: TFunction, prev: number | null, delta: Delta | null, goodDirection: 'up' | 'down', value: string): DeltaChip | null {
  if (prev == null || !delta) return null;
  if (delta.direction === 'flat') return { text: t('dashboard:delta.flat'), tone: 'neutral' };
  return {
    text: t(delta.direction === 'down' ? 'dashboard:delta.down' : 'dashboard:delta.up', { value }),
    tone: delta.direction === goodDirection ? 'ok' : 'neutral',
  };
}

/** The labeled line's points + benchmark — the span from the first measured month to the last; undefined until two months have a value, so an empty building keeps the quiet tile. */
function headlineTrend(
  trend: ReportingOverview['trend'], pick: (m: ReportingOverview['trend'][number]) => number | null,
  text: (v: number) => string, benchmark: number | null, benchmarkLabel: string,
): HeadlineTrend | undefined {
  const all = trend.map(pick);
  const first = firstIndex(all);
  const last = lastIndex(all);
  // The line runs edge to edge over the measured span — first known month to last — never a stub that ends halfway.
  if (first == null || last == null || last - first < 1) return undefined;
  const points = trend.slice(first, last + 1).map((m) => {
    const value = pick(m);
    return { label: monthLabel(m.month), tip: `${monthTip(m.month)} · ${value == null ? '—' : text(value)}`, value, text: value == null ? '—' : text(value) };
  });
  return { points, benchmark, benchmarkLabel };
}

export function buildDashboardView(o: ReportingOverview, { money, t, scope }: { money: boolean; t: TFunction; scope: DrillScope }): DashboardView {
  const p = o.pulse;
  const pct = (n: number, of: number) => Math.round((n / of) * 100);
  const dText = (d: number) => t('dashboard:units.d', { value: days1(d) });
  const deltaHoursText = (a: number, b: number) =>
    a < 24 && b < 24 ? t('dashboard:units.h', { value: Math.round(Math.abs(a - b)) }) : dText(Math.abs(hoursToDays(a - b)));
  const industryCell = (r: IndustryReference) => t('dashboard:tile.industryCell', { text: r.text, source: r.sourceShort, year: r.year });
  const series = (field: 'mttrHours' | 'satisfaction' | 'spend', map: (v: number) => number = (v) => v) =>
    o.trend.map((m) => m[field]).filter((v): v is number => v != null).map(map);

  // ── Headline three ──
  const noCompleted = o.workOrders.completed === 0;
  const headlines: HeadlineTile[] = [
    {
      key: 'timeToComplete',
      label: t('dashboard:tile.timeToComplete'),
      // Nothing completed → "—" and the sentence, never "0 hours · of 0 completed" (the phone's
      // rule; a building with no completed work read "0 hours ▼ 1 h" on staging, 2026-09-17).
      // Otherwise the same rule as fmtHours, split into value + unit for the tile's anatomy.
      value: noCompleted ? '—' : p.mttrHours < 24 ? String(Math.round(p.mttrHours)) : days1(hoursToDays(p.mttrHours)),
      unit: noCompleted ? undefined : p.mttrHours < 24 ? t('dashboard:units.hours') : t('dashboard:units.days'),
      // The difference in the unit BOTH periods read in: hours when both are under a day, else
      // days — and a difference that rounds to nothing reads "no change", never "▼ 0 h".
      delta: noCompleted ? null
        : p.mttrPrevHours != null && p.mttrHours < 24 && p.mttrPrevHours < 24 && Math.round(Math.abs(p.mttrHours - p.mttrPrevHours)) === 0
          ? deltaChip(t, p.mttrPrevHours, { pct: 0, direction: 'flat' }, 'down', '')
          : deltaChip(t, p.mttrPrevHours, p.mttrDelta, 'down', p.mttrPrevHours == null ? '' : deltaHoursText(p.mttrHours, p.mttrPrevHours)),
      context: noCompleted ? t('dashboard:empty.completed') : t('dashboard:tile.timeToCompleteContext', { count: o.workOrders.completed }),
      lastYearLabel: t('dashboard:tile.lastYear'),
      lastYear: p.mttrLastYear == null ? '—' : fmtHours(t, p.mttrLastYear),
      industry: industryCell(o.industry.timeToComplete),
      spark: series('mttrHours', hoursToDays),
      // Plotted in hours (the series' own unit); the reference is in days.
      trend: headlineTrend(o.trend, (m) => m.mttrHours, (v) => fmtHours(t, v), o.industry.timeToComplete.value * 24,
        `${t('dashboard:tile.industry')} ${o.industry.timeToComplete.text}`),
    },
    {
      key: 'residentRating',
      label: t('dashboard:tile.residentRating'),
      value: p.satisfactionCount > 0 ? p.satisfaction.toFixed(1) : '—',
      unit: t('dashboard:units.of5'),
      delta: p.satisfactionPrev != null && Math.abs(p.satisfaction - p.satisfactionPrev) < 0.05
        ? deltaChip(t, p.satisfactionPrev, { pct: 0, direction: 'flat' }, 'up', '')
        : deltaChip(t, p.satisfactionPrev, p.satisfactionDelta, 'up', p.satisfactionPrev == null ? '' : Math.abs(p.satisfaction - p.satisfactionPrev).toFixed(1)),
      context: p.satisfactionCount > 0
        ? t('dashboard:tile.residentRatingContext', { ratings: p.satisfactionCount, completed: o.workOrders.completed })
        : t('dashboard:empty.ratings'),
      lastYearLabel: t('dashboard:tile.lastYear'),
      lastYear: p.satisfactionLastYear == null ? '—' : p.satisfactionLastYear.toFixed(1),
      industry: industryCell(o.industry.residentRating),
      spark: series('satisfaction'),
    },
  ];
  if (money && p.spend != null) {
    headlines.push({
      key: 'spend',
      label: t('dashboard:tile.spend'),
      value: fmtMoney(p.spend),
      // The difference in dollars, as the phone prints it — an absolute difference like the hours and rating chips.
      delta: deltaChip(t, p.spendPrev, p.spendDelta, 'down', p.spendPrev == null ? '' : fmtMoney(Math.abs(p.spend - p.spendPrev))),
      context: p.spendPerUnit == null
        ? t('dashboard:tile.spendContextNoUnits')
        : t('dashboard:tile.spendContext', { perUnit: fmtMoney(p.spendPerUnit), units: p.liveUnits }),
      lastYearLabel: t('dashboard:tile.lastYear'),
      lastYear: p.spendLastYear == null ? '—' : fmtMoney(p.spendLastYear),
      industry: industryCell(o.industry.spendPerUnitMonth),
      spark: series('spend'),
      // The reference is per unit per month; the line plots monthly totals, so it is scaled by the units.
      trend: (() => {
        const bench = spendBenchmarkTotal(o.industry.spendPerUnitMonth.value, p.liveUnits);
        return headlineTrend(o.trend, (m) => m.spend, fmtMoneyShort, bench,
          bench == null ? '' : `${t('dashboard:tile.industry')} ${fmtMoneyShort(bench)}`);
      })(),
    });
  }

  // ── The strip ──
  const ai = o.aiActivity;
  const handled = ai.total > 0;
  const insp = o.inspections;
  const strip: StripTileView[] = [
    {
      key: 'openWorkOrders', sectionId: 'sec-work-orders',
      label: t('dashboard:strip.openWorkOrders'),
      value: String(p.openWorkOrders), danger: false,
      pill: p.overdue > 0 ? t('dashboard:strip.overdue', { count: p.overdue }) : undefined,
      why: p.openOldestDays == null ? t('dashboard:strip.noneOpen') : t('dashboard:strip.oldest', { count: p.openOldestDays }),
    },
    {
      key: 'emergencies', sectionId: 'sec-work-orders',
      label: t('dashboard:strip.emergencies'),
      value: String(p.emergencies), danger: p.emergencies > 0, why: t('dashboard:strip.openRightNow'),
    },
    {
      key: 'handledByAi', sectionId: 'sec-handled-by-ai',
      label: t('dashboard:strip.handledByAi'),
      value: handled ? t('dashboard:units.pct', { value: pct(ai.becameWorkOrders, ai.total) }) : '—', danger: false,
      why: handled ? t('dashboard:strip.handledOf', { count: ai.total }) : t('dashboard:empty.aiRequests'),
      nodata: !handled,
    },
    {
      key: 'makeReadyInProgress', sectionId: 'sec-make-ready',
      label: t('dashboard:strip.makeReady'),
      value: String(p.makeReadyInProgress), danger: false,
      why: p.makeReadyMedianDays == null ? t('dashboard:empty.turns') : t('dashboard:strip.medianTurn', { value: days1(p.makeReadyMedianDays) }),
      nodata: p.makeReadyMedianDays == null,
    },
    {
      key: 'inspectionsOnTime', sectionId: 'sec-inspections',
      label: t('dashboard:strip.inspectionsOnTime'),
      value: insp.onTimeEligible > 0 && insp.onTimePct != null ? t('dashboard:units.pct', { value: insp.onTimePct }) : '—', danger: false,
      why: insp.onTimeEligible > 0 ? t('dashboard:strip.onTimeOf', { n: insp.onTimeCount, m: insp.onTimeEligible }) : t('dashboard:empty.inspections'),
      nodata: insp.onTimeEligible === 0,
    },
    {
      key: 'vendorOnTime', sectionId: 'sec-cost-vendors',
      label: t('dashboard:strip.vendorOnTime'),
      value: o.vendorOnTime.n > 0 && o.vendorOnTime.pct != null ? t('dashboard:units.pct', { value: Math.round(o.vendorOnTime.pct) }) : '—', danger: false,
      why: o.vendorOnTime.n > 0 ? t('dashboard:strip.visits', { count: o.vendorOnTime.n }) : t('dashboard:empty.visits'),
      // The drawer's capture note is the explanation; `/vendors` never mentioned the arrival stamp. An empty href = open the drawer.
      link: o.vendorOnTime.n > 0 ? undefined : { label: t('dashboard:strip.howArrivals'), href: '' },
      nodata: o.vendorOnTime.n === 0,
    },
  ];

  // ── Insights ──
  // The sentence in the viewer's language, from the facts the server ships (its `text` is
  // English — the report PDF's); a row without facts (an older server) keeps the text.
  const insightText = (i: ReportingOverview['insightDetails'][number]): string => {
    const f = i.facts;
    if (!f) return i.text;
    switch (i.kind) {
      case 'trade': return t('dashboard:insights.sentence.trade', { label: categoryLabel(i.key), days: f.days, mult: f.mult });
      case 'vendor': return f.spend != null
        ? t('dashboard:insights.sentence.vendorSpend', { label: i.label, pct: f.pct, visits: f.visits, spend: fmtMoney(f.spend) })
        : t('dashboard:insights.sentence.vendor', { label: i.label, pct: f.pct, visits: f.visits });
      case 'category': return t('dashboard:insights.sentence.category', { label: categoryLabel(i.key), share: f.share, amount: fmtMoney(f.amount ?? 0) });
    }
  };
  const insights: InsightView[] = o.insightDetails.slice(0, 3).map((i) => (
    i.kind === 'vendor'
      ? { text: insightText(i), linkLabel: t('dashboard:insights.openVendor'), href: `/vendors/${i.key}` }
      : {
        text: insightText(i),
        linkLabel: t(i.kind === 'trade' ? 'dashboard:insights.seeWorkOrders' : 'dashboard:insights.seeSpend', { label: categoryLabel(i.key) }),
        // A trade's speed counts DONE jobs — the same door as its Speed-by-trade row; a category's spend
        // counts the bills approved in the period — the invoices door.
        href: i.kind === 'trade' ? workOrdersHref(scope, { category: i.key, status: 'DONE' }) : invoicesHref(scope, { category: i.key }),
      }
  ));

  // ── Work orders ──
  const slowest = o.speedByTrade[0];
  const speedMax = Math.max(...o.speedByTrade.map((r) => r.avgDays), 1);
  const medianDays = hoursToDays(p.mttrHours);
  const workOrders: DashboardView['workOrders'] = {
    answer: slowest
      ? t('dashboard:wo.answer', { open: p.openWorkOrders, overdue: p.overdue, trade: categoryLabel(slowest.category) })
      : t('dashboard:wo.answerNoTrade', { open: p.openWorkOrders, overdue: p.overdue }),
    byStatusSub: t('dashboard:wo.byStatusSub', { count: p.openWorkOrders }),
    byStatus: o.workOrders.byStatus.map((s) => ({
      key: s.status, label: statusLabel(s.status), value: s.count, valueText: String(s.count),
      href: workOrdersHref(scope, { status: s.status }, false),
    })),
    // The row's raw `category` (I-3) is the key it links with; the label is the web's own translation of it.
    speed: o.speedByTrade.map((r) => ({
      key: r.category, label: categoryLabel(r.category), n: t('dashboard:speed.n', { count: r.count }),
      // The one duration rule: a trade whose median is under a day reads in hours, never "0.0 d".
      value: r.avgDays, valueText: fmtHours(t, r.avgDays * 24),
      // The row counts COMPLETED jobs — the list it opens is the done population, never the list's OPEN default.
      href: workOrdersHref(scope, { category: r.category, status: 'DONE' }),
    })),
    speedReference: o.speedByTrade.length && p.mttrHours > 0
      ? { at: Math.min(1, medianDays / speedMax), label: t('dashboard:speed.medianMark', { value: fmtHours(t, p.mttrHours) }) }
      : null,
    trend: o.trend.map((m) => ({ month: m.month, created: m.count, completed: m.completed })),
  };

  // ── Handled by AI ──
  const channels: BarRow[] = [
    { key: 'voice', label: t('dashboard:ai.voiceCalls'), value: ai.voiceCalls },
    { key: 'sms', label: t('dashboard:ai.sms'), value: ai.sms },
    { key: 'email', label: t('dashboard:ai.email'), value: ai.email },
    { key: 'chat', label: t('dashboard:ai.inAppChat'), value: ai.inAppChat },
  ].map((c) => ({ ...c, valueText: String(c.value) })).sort((a, b) => b.value - a.value);
  const noVisitTotal = ai.closedByAiTriage + ai.residentSelfService;
  const aiView: DashboardView['ai'] = {
    answer: handled
      ? t('dashboard:ai.answer', { became: ai.becameWorkOrders, total: ai.total, pct: pct(ai.becameWorkOrders, ai.total), afterHours: ai.outsideHours })
      : t('dashboard:empty.aiRequests'),
    channels,
    channelsFoot: t('dashboard:ai.channelsFoot', { total: ai.total, became: ai.becameWorkOrders }),
    afterHours: handled ? t('dashboard:ai.afterHoursValue', { total: ai.total, pct: pct(ai.outsideHours, ai.total) }) : t('dashboard:empty.aiRequests'),
    // No link: the list's "resolved by AI" chip is a wider population (any AI/KB
    // resolution, disputed or recurred included) than this count (audit p3-3).
    noVisit: {
      value: String(noVisitTotal),
      pills: [t('dashboard:ai.closedByAiTriage', { count: ai.closedByAiTriage }), t('dashboard:ai.residentSelfService', { count: ai.residentSelfService })],
    },
  };

  // ── Make-ready ──
  const makeReady: DashboardView['makeReady'] = {
    answer: [
      t('dashboard:makeReady.answerTurns', { count: p.makeReadyInProgress }),
      p.makeReadyMedianDays == null ? null
        : p.makeReadyMedianPrevDays == null
          ? t('dashboard:makeReady.answerMedian', { value: days1(p.makeReadyMedianDays) })
          : t('dashboard:makeReady.answerMedianFrom', { value: days1(p.makeReadyMedianDays), prev: days1(p.makeReadyMedianPrevDays) }),
    ].filter(Boolean).join(' · '),
    turnsSub: o.makeReady.list.length < p.makeReadyInProgress
      ? t('dashboard:makeReady.turnsSubCapped', { count: p.makeReadyInProgress, shown: o.makeReady.list.length })
      : t('dashboard:makeReady.turnsSub', { count: p.makeReadyInProgress }),
    allTurns: o.makeReady.list.length < p.makeReadyInProgress
      ? { label: t('dashboard:makeReady.allTurns', { count: p.makeReadyInProgress }), href: makeReadyHref(scope) }
      : null,
    turns: o.makeReady.list.map((u, i) => ({
      // Two buildings can both have a "Unit 101" — on an org-wide page the row names the building too.
      key: `${u.unit}-${i}`, unit: scope.propertyId || !u.property ? u.unit : `${u.unit} · ${u.property}`, days: t('dashboard:makeReady.daysIn', { count: u.days }),
      stage: u.stage ? t(`makeReady:stages.${u.stage}`, { defaultValue: u.stage }) : '—',
      href: makeReadyHref(scope, u.stage),
    })),
  };

  // ── Inspections ──
  const inspections: DashboardView['inspections'] = {
    answer: t('dashboard:inspections.answer', { completed: insp.completed, onTime: insp.onTimeCount, findings: insp.findingsTotal, raised: insp.findingsRaised }),
    stats: {
      completed: String(insp.completed),
      onTime: insp.onTimeEligible > 0 && insp.onTimePct != null ? t('dashboard:units.pct', { value: insp.onTimePct }) : '—',
      onTimeSub: insp.onTimeEligible > 0 ? t('dashboard:inspections.nOfM', { n: insp.onTimeCount, m: insp.onTimeEligible }) : t('dashboard:empty.inspections'),
      findings: String(insp.findingsTotal),
      raised: String(insp.findingsRaised),
      raisedSub: t('dashboard:inspections.fromFindings'),
    },
    byType: insp.byType.map((r) => ({ type: r.type, label: inspectionTypeLabel(r.type), count: r.count, href: inspectionsHref(scope, { type: r.type }) })),
  };

  // ── Cost & vendors (money only) ──
  let cost: DashboardView['cost'] = null;
  if (money && o.spend.total != null && p.spend != null) {
    const total = o.spend.total;
    const cats = [...o.spend.category].sort((a, b) => b.amount - a.amount);
    const top = cats[0];
    cost = {
      answer: top && total > 0
        ? (p.spendPrev == null
          // No previous period to compare with — say the number, never "No change".
          ? t('dashboard:cost.answerNoPrev', { spend: fmtMoney(p.spend), category: categoryLabel(top.category), share: pct(top.amount, total) })
          : t('dashboard:cost.answer', { spend: fmtMoney(p.spend), delta: p.spendDelta && p.spendDelta.direction !== 'flat' ? t(p.spendDelta.direction === 'down' ? 'dashboard:cost.under' : 'dashboard:cost.over', { pct: p.spendDelta.pct }) : t('dashboard:cost.flat'), category: categoryLabel(top.category), share: pct(top.amount, total) }))
        : t('dashboard:cost.answerNoSpend', { spend: fmtMoney(p.spend) }),
      total: fmtMoney(total),
      // The bills behind the bar — approved in the period, whatever the job's age (the invoices door).
      byCategory: cats.map((c) => ({ key: c.category, label: categoryLabel(c.category), value: c.amount, valueText: fmtMoney(c.amount), href: invoicesHref(scope, { category: c.category }) })),
      trend: o.trend.map((m) => ({ label: monthLabel(m.month), tip: `${monthTip(m.month)} · ${m.spend == null ? '—' : fmtMoneyShort(m.spend)}`, value: m.spend })),
      vendors: [...o.vendors].sort((a, b) => (b.spend ?? 0) - (a.spend ?? 0)).map((v) => ({
        id: v.id, name: v.name, jobs: v.jobs,
        rating: v.ratingCount > 0 ? `${v.rating.toFixed(1)} (${v.ratingCount})` : '—',
        onTime: v.onTimePct == null ? '—' : `${t('dashboard:units.pct', { value: Math.round(v.onTimePct) })} (${v.onTimeN})`,
        spend: v.spend == null ? '—' : fmtMoney(v.spend),
        needsAttention: v.needsAttention,
        href: `/vendors/${v.id}`,
      })),
    };
  }

  return { headlines, strip, insights, workOrders, ai: aiView, makeReady, inspections, cost };
}

/** The four tables of the page as ONE csv — each block preceded by its title row (⋯ → "CSV of this page's tables"). */
export function dashboardTablesCsv(view: DashboardView, t: TFunction): { header: string[]; rows: (string | number)[][] } {
  const rows: (string | number)[][] = [];
  const block = (title: string, cols: string[], body: (string | number)[][]) => {
    if (rows.length) rows.push([]);
    rows.push([title], cols, ...body);
  };
  block(t('dashboard:wo.byStatusTitle'), [t('dashboard:csv.status'), t('dashboard:csv.count')], view.workOrders.byStatus.map((r) => [r.label, r.value]));
  block(t('dashboard:speed.title'), [t('dashboard:csv.trade'), t('dashboard:csv.completed'), t('dashboard:csv.medianDays')], view.workOrders.speed.map((r) => [r.label, r.n ?? '', r.value]));
  if (view.cost) {
    block(t('dashboard:cost.byCategoryTitle'), [t('dashboard:csv.category'), t('dashboard:csv.spend')], view.cost.byCategory.map((r) => [r.label, r.value]));
    block(t('dashboard:vendors.title'), [t('dashboard:vendors.colVendor'), t('dashboard:vendors.colJobs'), t('dashboard:vendors.colRating'), t('dashboard:vendors.colOnTime'), t('dashboard:vendors.colSpend'), t('dashboard:vendors.needsAttention')],
      view.cost.vendors.map((v) => [v.name, v.jobs, v.rating, v.onTime, v.spend, v.needsAttention ? t('dashboard:vendors.needsAttention') : '']));
  }
  // The first row IS the first block's title; downloadCsv prints header then rows.
  const [header, ...rest] = rows;
  return { header: header as string[], rows: rest };
}
